import { Input, InputRef, Spin } from 'antd';
import { useCombobox, UseComboboxActions } from 'downshift';
import debounce from 'lodash.debounce';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import theme from '../../../assets/theme';

// Default filter function - defined before the component
function defaultFilterItems<T>(
  items: T[],
  inputValue: string,
  itemToString: (item: T | null) => string,
) {
  return items.filter(item =>
    itemToString(item)
      .toLowerCase()
      .includes(inputValue.toLowerCase()),
  );
}

export interface TypeAheadInputRef<T> {
  input?: InputRef;
  dropdown?: UseComboboxActions<T>;
}

interface TypeAheadInputProps<T> {
  items?: T[];
  itemToString: (item: T | null) => string;
  MenuItemChildren?: React.ComponentType<{ item: T; inputValue: string }>;
  onSelect: (selectedItem: T | null) => void;
  onChange?: (inputValue: string) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  disabled?: boolean;
  showClearButton?: boolean;
  onClear?: () => void;
  onFetchError?: (data: { query: string; maxResults: number }, error: unknown) => void;
  error?: string;
  fluid?: boolean;
  initialSelectedItem?: T | null;
  initialInputValue?: string;
  filterItems?: (
    items: T[],
    inputValue: string,
    itemToString: (item: T | null) => string,
  ) => T[];
  // New props for async fetching
  fetchSuggestions?: (query: string, limit?: number) => Promise<T[]>;
  debounceMs?: number;
  minChars?: number;
  // New props per requirements
  showResultsHeader?: boolean;
  showNoResultsMessage?: boolean;
  footerComponent?: React.ReactNode;
  maxResults?: number;
}

export function TypeAheadInputInner<T>(
  {
    items = [],
    itemToString,
    MenuItemChildren,
    onSelect,
    onChange,
    label,
    placeholder,
    className,
    style,
    labelStyle,
    disabled = false,
    showClearButton = false,
    onClear,
    onFetchError,
    error,
    fluid = false,
    initialSelectedItem = null,
    initialInputValue = '',
    filterItems = defaultFilterItems,
    // New props with defaults
    fetchSuggestions,
    debounceMs = 300,
    minChars = 2,
    // New features
    showResultsHeader = false,
    showNoResultsMessage = true,
    footerComponent,
    maxResults = 10,
  }: TypeAheadInputProps<T>,
  ref: React.Ref<TypeAheadInputRef<T>>,
) {
  const [inputItems, setInputItems] = useState(items);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Debounced fetch function
  const debouncedFetch = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!fetchSuggestions || query.length < minChars) {
          return;
        }

        setIsLoading(true);
        try {
          const results = await fetchSuggestions(query, maxResults);
          setInputItems(results);
          setHasSearched(true);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          onFetchError && onFetchError({ query, maxResults }, error);
        } finally {
          setIsLoading(false);
        }
      }, debounceMs),
    [fetchSuggestions, minChars, debounceMs, maxResults, onFetchError],
  );

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  const {
    isOpen,
    reset,
    openMenu,
    closeMenu,
    toggleMenu,
    selectItem,
    setHighlightedIndex,
    setInputValue,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    selectedItem,
    inputValue: _inputValue,
    getItemProps,
  } = useCombobox({
    items: inputItems,
    itemToString,
    initialSelectedItem,
    initialInputValue,
    onSelectedItemChange: ({ selectedItem }) => {
      onSelect(selectedItem);
    },
    onInputValueChange: ({ inputValue }) => {
      if (fetchSuggestions) {
        // If we're using async fetching
        if (inputValue && inputValue.length >= minChars) {
          debouncedFetch(inputValue);
        } else {
          setInputItems([]);
          setHasSearched(false);
        }
      } else {
        // If we're using local filtering
        const filteredItems = filterItems(items, inputValue || '', itemToString);
        setInputItems(filteredItems);
        setHasSearched(true);
      }

      if (onChange) {
        onChange(inputValue || '');
      }
    },
  });

  // Setup Ref to input
  const inputRef = useRef<InputRef>(null);

  const typeAheadInputApi: TypeAheadInputRef<T>['dropdown'] = {
    reset,
    openMenu,
    closeMenu,
    toggleMenu,
    selectItem,
    setHighlightedIndex,
    setInputValue,
  };

  useImperativeHandle(ref, () => ({
    input: inputRef.current ?? undefined,
    dropdown: typeAheadInputApi,
  }));

  // Determine if dropdown should be open - after useCombobox so isOpen and _inputValue are defined
  const shouldShowDropdown =
    isOpen &&
    (inputItems.length > 0 ||
      (hasSearched && _inputValue && _inputValue.length >= minChars) ||
      (footerComponent && hasSearched && _inputValue && _inputValue.length >= minChars));

  // Jumping through hoops to avoid downshift errors from refs
  const { ref: downshiftRef, ...inputProps } = getInputProps() as ReturnType<
    typeof getInputProps
  > & { ref: React.Ref<unknown> };

  return (
    <Wrapper $fluid={fluid} className={className} style={style}>
      {label && <Label style={labelStyle}>{label}</Label>}
      <ComboboxContainer>
        <EnhancedInput
          {...inputProps}
          ref={mergeRefs(inputRef, downshiftRef)}
          placeholder={placeholder}
          disabled={disabled || showClearButton}
          $disabled={disabled || showClearButton}
          $error={!!error}
          $fluid={fluid}
          suffix={
            !showClearButton ? (
              <SpinContainer>
                {isLoading ? (
                  <Spin size="small" />
                ) : (
                  <span style={{ width: '16px', height: '16px' }} />
                )}
              </SpinContainer>
            ) : (
              <a onClick={onClear} style={{ color: theme.colors.blue }}>
                Change
              </a>
            )
          }
        />

        <Menu {...getMenuProps()} $isOpen={isOpen} $hasContent={shouldShowDropdown}>
          {isOpen && (
            <>
              {inputItems.length > 0 ? (
                <>
                  {showResultsHeader && (
                    <ResultsHeader>Top results ({inputItems.length})</ResultsHeader>
                  )}
                  {inputItems.map((item, index) => (
                    <MenuItem
                      key={`${itemToString(item)}-${index}`}
                      $highlighted={highlightedIndex === index}
                      $selected={selectedItem === item}
                      {...getItemProps({ item, index })}
                    >
                      {MenuItemChildren ? (
                        <MenuItemChildren item={item} inputValue={_inputValue} />
                      ) : (
                        itemToString(item)
                      )}
                    </MenuItem>
                  ))}
                </>
              ) : (
                hasSearched &&
                _inputValue &&
                _inputValue.length >= minChars &&
                showNoResultsMessage && (
                  <NoResultsMessage>No results found</NoResultsMessage>
                )
              )}

              {/* Footer component - always show when dropdown is open and search criteria met */}
              {footerComponent &&
                hasSearched &&
                _inputValue &&
                _inputValue.length >= minChars && (
                  <FooterContainer
                    tabIndex={-1}
                    onKeyDown={e => {
                      // Prevent default to avoid double navigation
                      e.stopPropagation();
                    }}
                  >
                    {footerComponent}
                  </FooterContainer>
                )}
            </>
          )}
        </Menu>
      </ComboboxContainer>
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </Wrapper>
  );
}

export const TypeAheadInput = React.forwardRef(TypeAheadInputInner) as <T>(
  props: TypeAheadInputProps<T> & { ref?: React.Ref<TypeAheadInputRef<T>> },
) => React.ReactElement;

const Wrapper = styled.div<{ $fluid: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ $fluid }) => ($fluid ? '100%' : 'auto')};
  position: relative;
  margin-bottom: 10px;
`;

const ComboboxContainer = styled.div`
  position: relative;
  width: 100%;
`;

const StyledInput = styled(Input)<{
  $error: boolean;
  $fluid: boolean;
  $disabled: boolean;
}>`
  width: ${({ $fluid }) => ($fluid ? '100%' : '250px')};
  height: 36px;
  border-radius: 4px;
  background-color: ${theme.colors.white};
  ${({ $error, $disabled }) =>
    $error && !$disabled
      ? css`
          border-color: ${theme.colors.red};

          &:focus,
          &:hover {
            border-color: ${theme.colors.red};
          }
        `
      : null}
  ${({ $error, $disabled }) =>
    $error && $disabled
      ? css`
          border-color: ${theme.colors.red};

          background-color: rgba(1, 0, 0, 0.05);

          &:focus,
          &:hover {
            border-color: ${theme.colors.red};
          }
        `
      : null}
`;

function mergeRefs<T = unknown>(
  ...refs: (React.Ref<T> | undefined)[]
): React.RefCallback<T> {
  return (value: T) => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref && typeof ref === 'object') {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

const EnhancedInput = forwardRef<InputRef, React.ComponentProps<typeof StyledInput>>(
  (props, ref) => {
    return <StyledInput {...props} ref={ref} />;
  },
);

EnhancedInput.displayName = 'EnhancedInput';

const Menu = styled.ul<{ $isOpen: boolean; $hasContent: boolean }>`
  position: absolute;
  z-index: 1000;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey};
  border-radius: 4px;
  margin-top: 2px;
  padding: 0;
  list-style: none;
  display: ${({ $isOpen, $hasContent }) => ($isOpen && $hasContent ? 'block' : 'none')};
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
`;

const MenuItem = styled.li<{ $highlighted: boolean; $selected: boolean }>`
  padding: 8px 12px;
  cursor: pointer;

  ${({ $highlighted }) =>
    $highlighted &&
    css`
      background-color: ${theme.colors.grey};
    `}

  ${({ $selected }) =>
    $selected &&
    css`
      background-color: ${theme.colors.blue};
      color: ${theme.colors.white};
    `}
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.colors.grey};
  margin-bottom: 5px;
`;

const ErrorLabel = styled.span`
  color: ${theme.colors.red};
  font-size: 12px;
  margin-top: 4px;
`;

const SpinContainer = styled.div`
  min-width: 24px;
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoResultsMessage = styled.div`
  padding: 8px 12px;
  color: ${theme.colors.grey};
  font-style: italic;
  text-align: center;
`;

const ResultsHeader = styled.div`
  padding: 4px 12px;
  color: ${theme.colors.grey};
  font-style: italic;
  text-align: center;
  font-size: 12px;
`;

const FooterContainer = styled.div`
  padding: 8px 12px;
  border-top: 1px solid ${theme.colors.grey};
  outline: none;
`;
